import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * NexButton — the central brand button (b13d T1).
 *
 * Before b13d buttons were inline-styled per page (hardcoded `#00E676` fills
 * with white text) which is why they went invisible in light mode. This is the
 * single source of truth for brand buttons and is theme-aware via design
 * tokens, so it stays legible in BOTH light and dark mode automatically.
 *
 * Exported as both `NexButton` and `Button` (alias) so existing imports keep
 * working; lives at ui/NexButton.tsx to avoid colliding with the shadcn
 * `ui/button.tsx` primitive. Siblings: NexPill, NexChipBox.
 *
 * Variants (per brief):
 *   primary     — dark-green border + dark-green text on light surface in light
 *                 mode; dark-green fill + light text in dark mode.
 *   secondary   — neutral surface, default (green) border, primary text.
 *   ghost       — transparent + green text; accent-subtle wash on hover.
 *   destructive — error border + error text; error fill on hover.
 *
 * All variants: 1px border in the brand-green family, radius var(--radius-md),
 * text always readable in both themes.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "font-semibold whitespace-nowrap select-none",
    "rounded-[var(--radius-md)] border",
    "transition-colors [transition-duration:var(--duration-base)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)]",
    "disabled:pointer-events-none disabled:opacity-40",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          // b13f hotfix: filled in BOTH themes. Outlined-on-white was invisible
          // on light backgrounds (e.g. LegalSplash modal) — every primary CTA
          // should be a loud, filled brand-green button with white text.
          "border-transparent",
          "bg-[var(--brand-green-700)] text-[var(--text-on-accent)]",
          "hover:bg-[var(--accent-pressed)]",
          "dark:bg-[var(--accent-fill)] dark:text-[var(--text-on-accent)]",
          "dark:hover:bg-[var(--accent-hover)]",
        ].join(" "),
        secondary: [
          "border-[var(--border-default)]",
          "bg-[var(--surface-sunken)] text-[var(--text-primary)]",
          "hover:border-[var(--brand-green-700)]",
        ].join(" "),
        ghost: [
          "border-transparent bg-transparent",
          "text-[var(--brand-green-on-light)] dark:text-[var(--accent-fill)]",
          "hover:bg-[var(--accent-subtle)]",
        ].join(" "),
        destructive: [
          "border-[var(--status-error)] bg-transparent text-[var(--status-error)]",
          "hover:bg-[var(--status-error)] hover:text-[var(--text-on-accent)]",
        ].join(" "),
      },
      size: {
        sm: "min-h-8 px-3 text-xs rounded-[var(--radius-sm)]",
        md: "min-h-10 px-4 text-sm",
        lg: "min-h-12 px-6 text-base rounded-[var(--radius-lg)]",
        block: "min-h-11 px-6 text-sm w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface NexButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const NexButton = React.forwardRef<HTMLButtonElement, NexButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        {...props}
      />
    );
  }
);
NexButton.displayName = "NexButton";

export { NexButton, NexButton as Button, buttonVariants };
